import * as React from "react";

import { AccordionContainer, Accordion, Breadcrumbs, BreadcrumbTemplateEnum, formatDateTime, LoadingSpinner, Page, PageHeader, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { RoleModel, Roles, UserContext } from "@app/user";
import { ClientLoginStatistics, ClientService, UserLoginInformation } from "@app/client";
import { QESButton, QESSelect, } from "@app/shared/qes-standards";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { orderBy } from "lodash";
import { QESCheckbox } from "../../shared/qes-standards/forms/Checkbox";
import { Forbidden } from "../../shared/ErrorPages";
import { ClientListItem } from "../../client/client";
import { ExportService } from "../../exports";
import { ExportFormatType } from "../../exports/enumerations";
import { toastInfo } from "../../exports/export-panel-body/toast";
import { JSX } from "react";

interface UserLoginTableData {
  name: string;
  userName: JSX.Element;
  roles: JSX.Element;
  daysSinceLastLogin: number;
  lastLoginDate: string;
  totalLogins: number;
  status: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const loggedInUsersColumnConfig: ColumnConfig = {
  daysSinceLastLogin: {
    displayText: "Days Since Last Login",
    testId: "days-since-last-login",
  },
  lastLoginDate: {
    displayText: "Last Login",
    testId: "last-login",
  },
  name: {
    displayText: "Name",
    testId: "name",
  },
  roles: {
    displayText: "Roles",
    noSort: true,
    testId: "roles",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
  totalLogins: {
    displayText: "Total Logins",
    testId: "total-logins",
  },
  userName: {
    displayText: "UserName (Email)",
    testId: "user-name",
  }
};

const neverLoggedInUsersColumnConfig: ColumnConfig = {
  name: {
    displayText: "Name",
    testId: "name",
  },
  roles: {
    displayText: "Roles",
    noSort: true,
    testId: "roles",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
  userName: {
    displayText: "UserName (Email)",
    testId: "user-name",
  }
};

interface LoginStatisticsRouteProps {
  clientId?: string;
}

type PropsType = RouteComponentProps<LoginStatisticsRouteProps>;

const take = 25;
const roleIdParamKey = "roleId";
const accountStatusParamKey = "accountStatus";
const includeGuestUsersParamKey = "includeGuestUsers";

export const LoginStatistics: React.FC<React.PropsWithChildren<PropsType>> = (props: PropsType) => {
  const urlParams = new URLSearchParams(window.location.search);
  const clientService = new ClientService();
  const history = useHistory();

  const [clientId, setClientId] = React.useState(props.match.params.clientId);
  const [clients, setClients] = React.useState<ClientListItem[]>([]);
  const [roles, setRoles] = React.useState<RoleModel[]>([]);
  const [loginStatistics, setLoginStatistics] = React.useState<ClientLoginStatistics>(null);
  const [loggedInUsersTableData, setLoggedInUsersTableData] = React.useState<UserLoginTableData[]>([]);
  const [skipLoggedInUsers, setSkipLoggedInUsers] = React.useState(0);
  const [neverLoggedInUsersTableData, setNeverLoggedInUsersTableData] = React.useState<UserLoginTableData[]>([]);
  const [skipNeverLoggedInUsers, setSkipNeverLoggedInUsers] = React.useState(0);
  const [includeGuestUsers, setIncludeGuestUsers] = React.useState(() => urlParams.get(includeGuestUsersParamKey) !== "false");

  //  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [reportExecuting, setReportExecuting] = React.useState(false);
  const [roleFilter, setRoleFilter] = React.useState(() => urlParams.get(roleIdParamKey) ?? "-1");

  const [statusFilter, setStatusFilter] = React.useState(() => urlParams.get(accountStatusParamKey) ?? "All");
  const user = React.useContext(UserContext);
  const userIsQuestAdmin = React.useRef(user.roles.includes(Roles.QUEST_ADMIN)).current;

  React.useEffect(() => {
    setLoading(true);

    clientService.getActiveClientsList().then((clients) => {
      setClients(clients);
      setLoading(false);
    });
  }, []);

  // pick the client id off the URL, if specified, and restrict the list of clients to only that single client
  React.useEffect(() => {
    if (!clientId && !userIsQuestAdmin) {
      setClientId(user.clientId.toString()); // non-questadmin, didn't specify client id in URL. Allow access only to their own  client
    }
  }, [props.match.params.clientId]);

  // reload roles when client id changes
  React.useEffect(() => {
    if (!clientId) {
      setLoginStatistics(null);
      return;
    }

    setLoading(true);
    setLoginStatistics(null);

    const clientService = new ClientService();
    clientService.getClientRoles(parseInt(clientId, 10)).then((roles) => {
      setRoles(roles);

      if ((roleFilter && roleFilter !== "-1") && (!roles.find((r) => r.roleId === parseInt(roleFilter, 10)))) {
        // Role filter is not present in the new list of Roles, set to All
        setRoleFilter("-1");
      }
    });

    clientService.getLoginStatisticsByClient(parseInt(clientId, 10), true).then((stats) => {
      setSkipLoggedInUsers(0);
      setSkipNeverLoggedInUsers(0);
      setLoginStatistics(stats);
      setLoading(false);
    });
  }, [clientId]);

  // update the tables anytime the role or status filter is changed
  React.useEffect(() => {
    if (loginStatistics) {
      setLoggedInUsersTableData(buildTableData(loginStatistics.loggedInUsers, false));
      setNeverLoggedInUsersTableData(buildTableData(loginStatistics.neverLoggedInUsers, false));
    } else {
      setLoggedInUsersTableData([]);
      setNeverLoggedInUsersTableData([]);
    }

    setStickyFilters();
  }, [loginStatistics, roleFilter, statusFilter, includeGuestUsers]);


  const rolesOptions = React.useMemo(() => {
    return [<option value="-1" key="-1">All Roles</option>, roles.map((r) => <option value={r.roleId} key={r.roleId}>{r.name}</option>)];
  }, [roles]);

  const clientsOptions = React.useMemo(() => {
    return [clients.map((c) => <option value={c.clientId} key={c.clientId}>{c.name}</option>)];
  }, [clients]);


  const setStickyFilters = () => {
    const searchParams = new URLSearchParams(window.location.search);

    if (roleFilter && roleFilter !== "-1") {
      searchParams.set(roleIdParamKey, roleFilter);
    } else {
      searchParams.delete(roleIdParamKey);
    }

    if (statusFilter && statusFilter !== "All") {
      searchParams.set(accountStatusParamKey, statusFilter);
    } else {
      searchParams.delete(accountStatusParamKey);
    }

    if (!includeGuestUsers) {
      searchParams.set(includeGuestUsersParamKey, "false");
    } else {
      searchParams.delete(includeGuestUsersParamKey);
    }

    // Update History
    const searchParamsStr = searchParams.toString();
    let currentPathname = "/admin/login-statistics";

    if (!!clientId) {
      currentPathname += `/${clientId}`;
    }

    history.replace(currentPathname + "?" + searchParamsStr);
  };

  const buildTableData = (data: UserLoginInformation[], clearFilter?: boolean): UserLoginTableData[] => {
    let filteredData = data;
    if (!clearFilter) {
      if (roleFilter) {
        filteredData = filteredData.filter((d) => roleFilter === "-1" || d.roles.find((r) => r.roleId === parseInt(roleFilter, 10)));
      }
      if (statusFilter) {
        filteredData = filteredData.filter((d) => statusFilter.toLowerCase() === "all" || (d.isActive && d.emailConfirmed && statusFilter.toLowerCase() === "active") || (!d.isActive && !d.emailConfirmed && statusFilter.toLowerCase() === "unconfirmed") || (!d.isActive && d.emailConfirmed && statusFilter.toLowerCase() === "inactive"));
      }

      // include guest logins only when requested
      filteredData = filteredData.filter((d) => (includeGuestUsers && d.isGuestLogin) || !d.isGuestLogin);
    }

    const now = Date.now();
    return filteredData.map<UserLoginTableData>((loginInfo) => {

      /* eslint-disable sort-keys */
      return {
        name: `${loginInfo.lastName}, ${loginInfo.firstName}`,
        userName: <div key={`${loginInfo.userName} (${loginInfo.email})`} style={{ maxWidth: "450px", minWidth: "300px", whiteSpace: "normal" }}>{`${loginInfo.userName} (${loginInfo.email})`}</div>,
        roles: <div style={{ maxWidth: "450px", minWidth: "300px", whiteSpace: "normal" }}>{loginInfo.roles.map((r) => r.name).join(", ")}</div>,
        lastLoginDate: formatDateTime(loginInfo.lastLoginDate),
        daysSinceLastLogin: loginInfo.lastLoginDate ? Math.max(0, Math.ceil((now - new Date(loginInfo.lastLoginDate).getTime()) / (1000 * 3600 * 24))) : 0,
        totalLogins: loginInfo.totalLogins,
        status: loginInfo.isActive ? <TooltipWrapper key="0" text="User is Activated"><i className="fa fa-unlock text-success" /></TooltipWrapper> : !loginInfo.emailConfirmed ? <TooltipWrapper key="1" text="Email is not confirmed"><i className="fa fa-envelope text-warning" /></TooltipWrapper> : <TooltipWrapper key="2" text="User is Deactivated"><i className="fa fa-lock text-danger" /></TooltipWrapper>,
        [wafflesKeyColumnName]: loginInfo.email.toString(),
      };
      /* eslint-enable sort-keys */
    });
  }

  const paginationPropsLoggedInUsers: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setSkipLoggedInUsers((pageNumber - 1) * take),
    recordsPerPage: take,
    skip: skipLoggedInUsers,
    totalRecords: loggedInUsersTableData.length,
  }

  const customSortLoggedInUsers = (records: UserLoginTableData[], key: keyof UserLoginTableData, sortDirection: SortDirections) => {
    // ignore `records` because they're undefined due to passThroughSorting/we're only sending a fraction of them at a time
    // and in this case SortDirections.None orders them alphabetically anyway, so we can just sort "asc"
    let sortedData: UserLoginTableData[] = [];
    switch (key) {
      case "name":
        sortedData = orderBy(loggedInUsersTableData, (d) => [d[key].toString().toLowerCase(), d["userName"]], [sortDirection === SortDirections.Desc ? "desc" : "asc", sortDirection === SortDirections.Desc ? "desc" : "asc"]);
        break;
      case "userName":
        sortedData = orderBy(loggedInUsersTableData, (d) => d[key].key, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "totalLogins":
      case "daysSinceLastLogin":
        sortedData = orderBy(loggedInUsersTableData, (d) => d[key], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "lastLoginDate":
        sortedData = orderBy(loggedInUsersTableData, (d) => new Date(d[key]), sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "status":
        sortedData = orderBy(loggedInUsersTableData, (d) => d[key].key, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
    }
    setLoggedInUsersTableData(sortedData);
    return records;
  }

  const paginationPropsNeverLoggedInUsers: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setSkipNeverLoggedInUsers((pageNumber - 1) * take),
    recordsPerPage: take,
    skip: skipNeverLoggedInUsers,
    totalRecords: neverLoggedInUsersTableData.length,
  }

  const customSortNeverLoggedInUsers = (records: UserLoginTableData[], key: keyof UserLoginTableData, sortDirection: SortDirections) => {
    // ignore `records` because they're undefined due to passThroughSorting/we're only sending a fraction of them at a time
    // and in this case SortDirections.None orders them alphabetically anyway, so we can just sort "asc"
    let sortedData: UserLoginTableData[] = [];
    switch (key) {
      case "name":
        sortedData = orderBy(neverLoggedInUsersTableData, (d) => [d[key].toString().toLowerCase(), d["userName"]], [sortDirection === SortDirections.Desc ? "desc" : "asc", sortDirection === SortDirections.Desc ? "desc" : "asc"]);
        break;
      case "userName":
        sortedData = orderBy(neverLoggedInUsersTableData, (d) => d[key].key, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "status":
        sortedData = orderBy(neverLoggedInUsersTableData, (d) => d[key].key, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
    }
    setNeverLoggedInUsersTableData(sortedData);
    return records;
  }

  const exportData = async () => {
    setReportExecuting(true);

    const exportService = new ExportService();
    const exportTitle = "User Login Statistics for " + clients.find(c => c.clientId === parseInt(clientId, 10)).name;

    await exportService.postUserLoginStatistics({
      clientId: parseInt(clientId, 10),
      eMailOnSuccess: false,
      exportName: exportTitle,
      formatType: ExportFormatType.Excel,
      includeGuestLogins: includeGuestUsers,
      runAsynchronous: true,
    });

    toastInfo(exportTitle);

    setReportExecuting(false);
  }

  if (!userIsQuestAdmin && clientId !== user.clientId.toString()) {
    return <Forbidden />;
  }

  return (
    <>
      <Page documentTitle="Login Statistics - Admin">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.Admin}
          items={[
            {
              iconClass: "fa-sign-in",
              text: "Login Statistics",
            },
          ]}
        />
        <PageInner>
          <PageHeader title="Login Statistics" />
          <PageSection>
            <div className="row mt-3">
              <div className="col-sm-3">
                <QESSelect
                  allowEmpty={true}
                  containerClassName="login-stats-client__input"
                  disabled={loading || !userIsQuestAdmin}
                  testId="login-stats__client-filter"
                  label="Client"
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Select a Client"
                  value={clientId}
                >
                  {clientsOptions}
                </QESSelect>
              </div>
              <div className="col-sm-3">
                <QESSelect
                  allowEmpty={false}
                  containerClassName="login-stats-role__input"
                  disabled={loading || !roles || !clientId}
                  testId="login-stats__role-filter"
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  value={roleFilter}
                >
                  {rolesOptions}
                </QESSelect>
              </div>
              <div className="col-sm-2">
                <QESSelect
                  allowEmpty={false}
                  containerClassName="login-stats-filters__input"
                  disabled={loading || !clientId}
                  testId="login-stats__status-filter"
                  label="Account Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  value={statusFilter}
                >
                  <option value="All">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Unconfirmed">Unconfirmed</option>
                </QESSelect>
              </div>
              <div className="col-sm-2 align-self-center">
                <QESCheckbox
                  label="Include Quest Guest User Logins"
                  containerClassName="mt-4"
                  checked={includeGuestUsers}
                  onChange={(e) => setIncludeGuestUsers(e.target.checked)}
                  disabled={loading || !clientId}
                  testId="login-stats__include-guest-users"
                />
              </div>
            </div>
            {loading && <div className="mt-3"><LoadingSpinner /></div>}
            {!loading && loginStatistics && (
              <>
                <div className="row mt-3">
                  <div className="col-sm-12">
                    <QESButton
                      qesType="secondary"
                      qesTestId="login-stats__export-button"
                      onClick={() => { exportData() }}
                      disabled={reportExecuting || loading || !clientId}
                    >
                      Export
                    </QESButton>
                  </div>
                </div>
                <div className="row mt-3">
                  <AccordionContainer className="col-sm-12">
                    <Accordion title="Summary" defaultOpen={true} testId="login-statistics__summary-accordion">
                      <p>Total Users: {loginStatistics.totalUsersLoggedIn}</p>
                      <p>Total User Logins: {loginStatistics.totalLogins}</p>
                      <p>Most Recent Login: {formatDateTime(loginStatistics.mostRecentLoginDate)}</p>
                    </Accordion>
                  </AccordionContainer>
                  <AccordionContainer className="col-sm-12">
                    <Accordion defaultOpen={true} title="Recently Logged-In Users" testId="login-statistics__recently-accordion">
                      <Waffles<UserLoginTableData>
                        columns={loggedInUsersColumnConfig}
                        customSort={customSortLoggedInUsers}
                        data={loggedInUsersTableData.slice(skipLoggedInUsers, skipLoggedInUsers + take)}
                        enablePagination={true}
                        fullWidth={true}
                        paginationProps={paginationPropsLoggedInUsers}
                        passThroughSorting={true}
                        testId="login-statistics-logged-in-users-table"
                      />
                    </Accordion>
                  </AccordionContainer>
                  <AccordionContainer className="col-sm-12">
                    <Accordion defaultOpen={true} title="Never Logged-In Users" testId="login-statistics__never-accordion">
                      <>
                        <Waffles<UserLoginTableData>
                          columns={neverLoggedInUsersColumnConfig}
                          customSort={customSortNeverLoggedInUsers}
                          data={neverLoggedInUsersTableData.slice(skipNeverLoggedInUsers, skipNeverLoggedInUsers + take)}
                          enablePagination={true}
                          fullWidth={true}
                          paginationProps={paginationPropsNeverLoggedInUsers}
                          passThroughSorting={true}
                          testId="login-statistics-never-logged-in-users-table"
                        />
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </>
            )}
          </PageSection>
        </PageInner>
      </Page>
    </>
  );
}